import { SortEnum } from "@finway-group/shared/lib/consts"
import {
    ApprovalScopeEnum,
    type Expense,
    ExpenseKindEnum,
    ExpenseStatusEnum,
    type FilterBaseInterface,
    FilterPresetEnum,
    OPOS_LIST_ELIGIBLE_EXPENSE_STATUSES,
    SubscriptionTypeEnum,
    TransactionFlowFilterEnum,
    TransactionTypeEnum,
} from "@finway-group/shared/lib/models"
import { ExpenseDocumentTypeEnum } from "@finway-group/shared/lib/models/expense/expenseDocumentType.enum"
import { ExpensePaymentOptionEnum } from "@finway-group/shared/lib/models/expense/expensePaymentOption.enum"
import { TransactionStatusEnum } from "@finway-group/shared/lib/models/transaction/transactionStatus.enum"
import { type SortOrder } from "antd/lib/table/interface"
import { ATTACHABLE_EXPENSE_STATUSES } from "~Shared/config/consts"
import { type DraggableTableDataSourceType } from "~Shared/hooks/table.hooks"
import { TableActionsTypes, type UpdateTableFilterType } from "~Shared/store/actions/tables/tableTypes.enum"
import { type TableConfigData, TablesEnum } from "~Shared/store/reducers/tableConfigReducer"
import type { Table } from "~Shared/store/reducers/tableReducer"
import { EXCLUDE_TRIP_FOLDER_FILTER_QUERY, isFolderExpense } from "~Shared/utils/expense.utils"
import { getFilterQueryString } from "~Shared/utils/filter.utils"
import { isSet } from "~Shared/utils/helper.utils"
import { type QueryParams, type ReactQueryTableData } from "../queries/consts"

/** Config for building a Table from query data (handles undefined data safely). */
export interface CreateTableFromQueryConfig {
    page: number
    limit: number
    sort: { field: string; order: string }
    search: string
    filter?: Record<string, string | number | boolean | object | Array<string>>
}

/**
 * Maps React Query table list data to the Table interface.
 * Returns a safe empty table when data is undefined (e.g. initial load).
 */
export const createTableFromQueryData = <T>(
    data: ReactQueryTableData<T> | undefined,
    config: CreateTableFromQueryConfig,
    meta: { isFetching: boolean; error: unknown },
): Table<T> => ({
    data: data
        ? {
              docs: data.docs,
              totalDocs: data.totalDocs,
              totalPages: data.totalPages,
              page: data.page,
              limit: data.limit,
              sort: config.sort,
              filter: (config.filter ?? {}) as Table<T>["data"]["filter"],
              search: config.search,
          }
        : {
              docs: [],
              totalDocs: 0,
              totalPages: 0,
              page: config.page,
              limit: config.limit,
              sort: config.sort,
              filter: (config.filter ?? {}) as Table<T>["data"]["filter"],
              search: config.search,
          },
    isFetching: meta.isFetching,
    error: meta.error,
    // React Query owns the fetching for these tables, so no redux fetch is ever queued.
    fetchRequestId: 0,
    startedFetchRequestId: 0,
})

export enum TableDocType {
    INBOX_INVOICE = "INBOX_INVOICE",
    EXPENSE = "EXPENSE",
    TRANSACTION = "TRANSACTION",
    SUBSCRIPTION = "SUBSCRIPTION",
    CARD = "CARD",
    COST_CENTER = "COST_CENTER",
    VENDOR = "VENDOR",
    WORKFLOW = "WORKFLOW",
    TRANSACTION_CATEGORY = "TRANSACTION_CATEGORY",
    TRIP_FOLDER = "TRIP_FOLDER",
    EXPORT_HISTORY = "EXPORT_HISTORY",
}

export const getTableActionType = (tableActionsTypes: TableActionsTypes, tablesEnum: TablesEnum): string => `${tableActionsTypes}_${tablesEnum}`

export const getSortOrderForColumn = (table: Table<any>, fieldName: string) => (table.data.sort.field === fieldName ? (table.data.sort.order as SortOrder) : undefined)

export const getSortOrderFromQueryParams = (queryParams: QueryParams, fieldName: string) => {
    const { sortField, sortOption } = queryParams
    const sortOrder = sortField === fieldName ? sortOption : undefined

    if (sortOrder) {
        return sortOrder === SortEnum.ASC ? "ascend" : "descend"
    }

    return undefined
}

export const buildQueryParamsFromTableConfig = (tableConfigData: TableConfigData): QueryParams => {
    const { sortOption, sortField } = parseTableConfigSorter(tableConfigData?.sorting)

    return {
        page: tableConfigData?.currentPage,
        limit: tableConfigData?.itemsPerPage,
        search: tableConfigData?.searchValue,
        sortField,
        sortOption,
    }
}

export const parseTableConfigSorter = (sorter: any) => {
    if (sorter.field && sorter.order) {
        return { sortOption: sorter.order === "ascend" ? SortEnum.ASC : SortEnum.DESC, sortField: sorter.field }
    }

    return { sortOption: undefined, sortOrder: undefined }
}

/**
 * Returns the sort that the table has when the user hasn't selected any particular sorting.
 */
export const getTableDefaultSort = (table: TablesEnum): { field: string; order: string } => {
    switch (table) {
        case TablesEnum.OPOS_LIST:
        case TablesEnum.OPOS_EXCLUDED_LIST:
            return { field: "invoiceDueDate", order: "ascend" }
        case TablesEnum.TODO_PURCHASE_APPROVAL:
        case TablesEnum.TODO_INVOICE_APPROVAL:
        case TablesEnum.TODO_DOCS_NEEDED:
        case TablesEnum.TODO_APPROVAL_PENDING:
        case TablesEnum.REVIEW_REQUESTS:
        case TablesEnum.PAY_AND_EXPORT_TO_BE_PAID:
        case TablesEnum.PAY_AND_EXPORT_TO_BE_EXPORTED:
        case TablesEnum.ALL_REQUESTS:
        case TablesEnum.TRIP_FOLDER_DRAFT:
            // oldest requests first
            return { field: "datePurchased", order: "ascend" }
        case TablesEnum.DONE_REQUESTS:
        case TablesEnum.IN_PROGRESS:
        case TablesEnum.ATTACH_EXPENSE:
            return { field: "datePurchased", order: "descend" }
        case TablesEnum.CARDS:
            return { field: "userObject.firstName", order: "ascend" }
        case TablesEnum.TRANSACTIONS_INFLOW:
        case TablesEnum.TRANSACTIONS_OUTFLOW:
        case TablesEnum.TRANSACTIONS_ARCHIVE:
        case TablesEnum.TRANSACTIONS_ALL:
            return { field: "transactionData.date", order: "descend" }
        case TablesEnum.EXPENSE_TRANSACTIONS:
            return { field: "probabilityForExpense", order: "descend" }
        case TablesEnum.CARD_TRANSACTIONS:
            return { field: "transactionData.date", order: "descend" }
        case TablesEnum.SUBSCRIPTIONS:
            return { field: "name", order: "ascend" }
        case TablesEnum.INBOX_INVOICE_MODAL_EXPENSES:
            return { field: "probability", order: "descend" }
        case TablesEnum.EXPORT_HISTORY:
            return { field: "exportDate", order: "descend" }
        case TablesEnum.INBOX_INVOICE:
            return { field: "uploadDate", order: "ascend" }
        default:
            return { field: "", order: "" }
    }
}

export const getSearchTargets = (table: TablesEnum, useGross: boolean) => {
    let searchTargets: Array<string> = []
    switch (table) {
        case TablesEnum.TODO_PURCHASE_APPROVAL:
        case TablesEnum.TODO_INVOICE_APPROVAL:
        case TablesEnum.TODO_DOCS_NEEDED:
        case TablesEnum.TODO_APPROVAL_PENDING:
        case TablesEnum.TRIP_FOLDER_DRAFT:
        case TablesEnum.REVIEW_REQUESTS:
        case TablesEnum.PAY_AND_EXPORT_TO_BE_PAID:
        case TablesEnum.PAY_AND_EXPORT_TO_BE_EXPORTED:
        case TablesEnum.DONE_REQUESTS:
        case TablesEnum.ALL_REQUESTS:
        case TablesEnum.IN_PROGRESS:
        case TablesEnum.ARCHIVE:
        case TablesEnum.INBOX_INVOICE_MODAL_EXPENSES:
        case TablesEnum.ATTACH_EXPENSE:
            searchTargets = [
                "approverName",
                "requesterName",
                "vendorName",
                "invoiceNumber",
                "description",
                "tripReason",
                "items.name",
                "expenseNumber",
                useGross ? "totalGrossPrice" : "totalNetPrice",
            ]
            break
        case TablesEnum.OPOS_LIST:
        case TablesEnum.OPOS_EXCLUDED_LIST:
            searchTargets = ["invoiceNumber", "expenseNumber", "vendorName", "totalGrossPrice", "totalNetPrice"]
            break
        case TablesEnum.SUBSCRIPTIONS:
            searchTargets = ["invoiceNumber", "description", "name", "items.name", useGross ? "totalGrossPrice" : "totalNetPrice"]
            break
        case TablesEnum.CARDS:
            searchTargets = ["cardName", "last4Digits", "nameOnCard", "userObject.firstName", "userObject.lastName"]
            break
        case TablesEnum.TRANSACTIONS_INFLOW:
        case TablesEnum.TRANSACTIONS_OUTFLOW:
        case TablesEnum.EXPENSE_TRANSACTIONS:
        case TablesEnum.TRANSACTIONS_ARCHIVE:
        case TablesEnum.TRANSACTIONS_ALL:
            searchTargets = ["transactionData.description", "transactionData.iban", "transactionData.amount", "transactionData.recipient"]
            break
        case TablesEnum.INBOX_INVOICE:
        case TablesEnum.INBOX_INVOICE_MODAL_INVOICES:
            searchTargets = ["fileName", "uploadedByObject.firstName", "uploadedByObject.lastName", "vendorName", "ocrResults.totalGrossPrice", "ocrResults.totalGrossPrice"]
            break
        default:
            searchTargets = []
    }

    return searchTargets
}

export const getTableDocType = (table: TablesEnum) => {
    switch (table) {
        case TablesEnum.INBOX_INVOICE:
        case TablesEnum.INBOX_INVOICE_MODAL_INVOICES:
            return TableDocType.INBOX_INVOICE
        case TablesEnum.TODO_PURCHASE_APPROVAL:
        case TablesEnum.TODO_INVOICE_APPROVAL:
        case TablesEnum.TODO_DOCS_NEEDED:
        case TablesEnum.TODO_APPROVAL_PENDING:
        case TablesEnum.REVIEW_REQUESTS:
        case TablesEnum.PAY_AND_EXPORT_TO_BE_PAID:
        case TablesEnum.PAY_AND_EXPORT_TO_BE_EXPORTED:
        case TablesEnum.OPOS_LIST:
        case TablesEnum.OPOS_EXCLUDED_LIST:
        case TablesEnum.DONE_REQUESTS:
        case TablesEnum.IN_PROGRESS:
        case TablesEnum.ALL_REQUESTS:
        case TablesEnum.SUBSCRIPTIONS:
        case TablesEnum.ARCHIVE:
        case TablesEnum.INBOX_INVOICE_MODAL_EXPENSES:
        case TablesEnum.ATTACH_EXPENSE:
            return TableDocType.EXPENSE
        case TablesEnum.TRIP_FOLDER_DRAFT:
            return TableDocType.TRIP_FOLDER
        case TablesEnum.CARDS:
            return TableDocType.CARD
        case TablesEnum.CARD_TRANSACTIONS:
        case TablesEnum.TRANSACTIONS_INFLOW:
        case TablesEnum.TRANSACTIONS_OUTFLOW:
        case TablesEnum.EXPENSE_TRANSACTIONS:
        case TablesEnum.TRANSACTIONS_ARCHIVE:
        case TablesEnum.TRANSACTIONS_ALL:
            return TableDocType.TRANSACTION
        case TablesEnum.COST_CENTERS:
            return TableDocType.COST_CENTER
        case TablesEnum.VENDORS:
            return TableDocType.VENDOR
        case TablesEnum.WORKFLOWS:
            return TableDocType.WORKFLOW
        case TablesEnum.TRANSACTION_CATEGORIES_INFLOW:
        case TablesEnum.TRANSACTION_CATEGORIES_OUTFLOW:
            return TableDocType.TRANSACTION_CATEGORY
        case TablesEnum.EXPORT_HISTORY:
            return TableDocType.EXPORT_HISTORY
        default:
            return ""
    }
}

let tablesEnumMapObject: any
/**
 * Concatenates the string passed in the first parameter with the enum key of the table passed in the second parameter.
 * The return value should match the enums declared here: {@link TableActionsTypes}.
 *
 * Example
 *  ```js
 * const tableType = getTableType("UPDATE_TABLE_PAGINATION", TablesEnum.EMPLOYEES)
 * ```
 */
export const getTableType = (tableType: string, table: TablesEnum) => {
    if (!tablesEnumMapObject) {
        tablesEnumMapObject = {}
        for (const [key, value] of Object.entries(TablesEnum)) {
            tablesEnumMapObject[value] = key
        }
    }
    const enumTable = tablesEnumMapObject[table]

    return `${tableType}_${enumTable}`
}

export const getFilterTableType = (tableType: UpdateTableFilterType, table: TablesEnum) => {
    // If table is from global dashboard filter return
    if (table === TablesEnum.TODO_INVOICE_APPROVAL) {
        return TableActionsTypes.UPDATE_DASHBOARD_TABLES_FILTER
    }

    return getTableType(tableType, table)
}

export const isTableFilterApplied = (filterObject: { [key: string]: string | object | Array<string> }): boolean => !!Object.keys(filterObject).length

export const generateTableFilterQueryString = (table: TablesEnum, useGross: boolean, filterObject: any) => {
    switch (table) {
        case TablesEnum.TODO_PURCHASE_APPROVAL:
        case TablesEnum.TODO_INVOICE_APPROVAL:
        case TablesEnum.TODO_DOCS_NEEDED:
        case TablesEnum.TODO_APPROVAL_PENDING:
        case TablesEnum.REVIEW_REQUESTS:
        case TablesEnum.PAY_AND_EXPORT_TO_BE_PAID:
        case TablesEnum.PAY_AND_EXPORT_TO_BE_EXPORTED:
        case TablesEnum.OPOS_LIST:
        case TablesEnum.OPOS_EXCLUDED_LIST:
        case TablesEnum.DONE_REQUESTS:
        case TablesEnum.IN_PROGRESS:
        case TablesEnum.ARCHIVE:
        case TablesEnum.INBOX_INVOICE:
        case TablesEnum.INBOX_INVOICE_MODAL_EXPENSES:
        case TablesEnum.INBOX_INVOICE_MODAL_INVOICES:
        case TablesEnum.CARDS:
        case TablesEnum.SUBSCRIPTIONS:
        case TablesEnum.POSSIBLE_MATCHES:
        case TablesEnum.TRANSACTIONS_INFLOW:
        case TablesEnum.TRANSACTIONS_OUTFLOW:
        case TablesEnum.TRANSACTIONS_ALL:
        case TablesEnum.EXPENSE_TRANSACTIONS:
        case TablesEnum.TRANSACTIONS_ARCHIVE:
        case TablesEnum.ATTACH_EXPENSE:
        case TablesEnum.TRIP_FOLDER_DRAFT:
            return getFilterQueryString(filterObject, useGross)
        case TablesEnum.ALL_REQUESTS:
            const draftQueryParam = !filterObject?.presets?.includes(FilterPresetEnum.DRAFT_TRIP_FOLDER) ? `&isDraft[nis]=true` : ``
            return draftQueryParam + getFilterQueryString(filterObject, useGross)

        default:
            return ""
    }
}

export const isVendorTableFilterApplied = (filterParameters: FilterBaseInterface | undefined) =>
    (filterParameters?.presets as Array<string>)?.length !== 0 ||
    (filterParameters && Object.keys(filterParameters).some((key) => key !== "presets" && isSet(filterParameters[key])))

export const getFolderIds = (expenses: Array<Expense>) => expenses.filter((expense: Expense) => isFolderExpense(expense)).map(({ _id }: any) => _id)

export const getChildrenQuery = ({ queryParams, applySearchQuery, folderIds }: { queryParams: any; applySearchQuery: boolean; folderIds: Array<string> }) =>
    Object.assign(queryParams, {
        // When fetching children, ignore the searchQueryString and search because we dont want to apply the searching when populating a folder's children
        ...(!applySearchQuery
            ? {
                  searchQueryString: "",
                  search: "",
              }
            : {}),

        filterQueryString: `&${EXCLUDE_TRIP_FOLDER_FILTER_QUERY}&deleted[is]=false&folderId[in]=${folderIds}`,
        limit: folderIds.length * 20,
        page: 1,
    })

export const buildTableQueryString = ({ table, loggedInUserId }: { table: TablesEnum; loggedInUserId: string }) => {
    const notDeletedQueryString = "&deleted[is]=false"
    const notArchivedQueryString = "&isArchived[is]=false"
    const archivedQueryString = "&isArchived[is]=true"
    switch (table) {
        case TablesEnum.TODO_PURCHASE_APPROVAL:
            return (
                `${notArchivedQueryString}&status[in]=${ExpenseStatusEnum.PURCHASE_PENDING}&requestedBy._id[ne]=${loggedInUserId}${notDeletedQueryString}` +
                `&includeUserIfIsApprover=true&approvalScope=${ApprovalScopeEnum.EXPENSE_PURCHASE}`
            )
        case TablesEnum.TODO_INVOICE_APPROVAL:
            return (
                `${notArchivedQueryString}&status[in]=${ExpenseStatusEnum.INVOICE_PENDING}&isDraft[nis]=true` +
                `&requestedBy._id[ne]=${loggedInUserId}${notDeletedQueryString}` +
                `&includeUserIfIsApprover=true&approvalScope=${ApprovalScopeEnum.EXPENSE_INVOICE}` +
                `&todoInvoiceApprovalTable=true`
            )
        case TablesEnum.INBOX_INVOICE_MODAL_EXPENSES:
        case TablesEnum.TODO_DOCS_NEEDED:
            return `${notArchivedQueryString}&status[in]=${ExpenseStatusEnum.DOCS_NEEDED}${notDeletedQueryString}&todoDocsNeededTable=true`
        case TablesEnum.TODO_APPROVAL_PENDING:
            // isDraft query using [nis]=true because we want get all with isDraft=false or isDraft=undefined
            // using [is]=false will return all with isDraft=false, but will not return isDraft=undefined
            return (
                `${notArchivedQueryString}&status[in]=${ExpenseStatusEnum.PURCHASE_PENDING},${ExpenseStatusEnum.INVOICE_PENDING}` +
                `&requestedBy._id[eq]=${loggedInUserId}${notDeletedQueryString}&isDraft[nis]=true` +
                `&excludeUserIfIsApprover=true`
            )

        case TablesEnum.PAY_AND_EXPORT_TO_BE_PAID:
            // credit notes and documents of a vendor payment are only paid in the vendor payment section
            return `${notArchivedQueryString}&status[in]=${ExpenseStatusEnum.REVIEWED}&documentType[ne]=${ExpenseDocumentTypeEnum.CREDIT_NOTE}&vendorPayment[set]=false${notDeletedQueryString}`
        case TablesEnum.OPOS_LIST:
            return `${notArchivedQueryString}${notDeletedQueryString}&${EXCLUDE_TRIP_FOLDER_FILTER_QUERY}&subscriptionType[ne]=${SubscriptionTypeEnum.INITIAL_REQUEST}&status[in]=${OPOS_LIST_ELIGIBLE_EXPENSE_STATUSES.join(",")}&isOposCleared[is]=false&isOposManuallyCleared[nis]=true&paymentOption[ne]=${ExpensePaymentOptionEnum.SMART_CARD}`
        // same open-item eligibility as the OPOS list, so an excluded expense drops off once it is no longer open
        case TablesEnum.OPOS_EXCLUDED_LIST:
            return `${notArchivedQueryString}${notDeletedQueryString}&${EXCLUDE_TRIP_FOLDER_FILTER_QUERY}&subscriptionType[ne]=${SubscriptionTypeEnum.INITIAL_REQUEST}&status[in]=${OPOS_LIST_ELIGIBLE_EXPENSE_STATUSES.join(",")}&isOposCleared[is]=false&isOposManuallyCleared[is]=true&paymentOption[ne]=${ExpensePaymentOptionEnum.SMART_CARD}`
        case TablesEnum.PAY_AND_EXPORT_TO_BE_EXPORTED:
            return `${notArchivedQueryString}&status[in]=${ExpenseStatusEnum.PAID}${notDeletedQueryString}`

        case TablesEnum.REVIEW_REQUESTS:
            return `${notArchivedQueryString}&status[eq]=${ExpenseStatusEnum.APPROVED}${notDeletedQueryString}`

        case TablesEnum.DONE_REQUESTS:
            // to not show duplicates with initial recurring expense
            return `${notArchivedQueryString}&status[in]=${ExpenseStatusEnum.DONE},${ExpenseStatusEnum.DECLINED}&isFullyProcessedSubscription[is]=false${notDeletedQueryString}`
        case TablesEnum.IN_PROGRESS:
            return `${notArchivedQueryString}&status[in]=${ExpenseStatusEnum.APPROVED},${ExpenseStatusEnum.REVIEWED},${ExpenseStatusEnum.PAID}&isFullyProcessedSubscription[is]=false${notDeletedQueryString}`
        case TablesEnum.ALL_REQUESTS:
            return `${notArchivedQueryString}&isFullyProcessedSubscription[is]=false${notDeletedQueryString}`
        case TablesEnum.ARCHIVE:
            return `${archivedQueryString}&isFullyProcessedSubscription[is]=false${notDeletedQueryString}`

        case TablesEnum.SUBSCRIPTIONS:
            return `&kind[eq]=${ExpenseKindEnum.SUBSCRIPTION}&subscriptionType[eq]=${SubscriptionTypeEnum.INITIAL_REQUEST}&status[in]=${ExpenseStatusEnum.PAID},${ExpenseStatusEnum.DONE}${notDeletedQueryString}`

        case TablesEnum.CARDS:
            return ""

        case TablesEnum.TRANSACTIONS_INFLOW:
            return `${notArchivedQueryString}&transactionFlow=${TransactionFlowFilterEnum.INFLOW}&transactionType[in]=${TransactionTypeEnum.BANK_TRANSACTION},${TransactionTypeEnum.CSV_TRANSACTION},${TransactionTypeEnum.CARD_TRANSACTION}`
        case TablesEnum.TRANSACTIONS_OUTFLOW:
            return `${notArchivedQueryString}&transactionFlow=${TransactionFlowFilterEnum.OUTFLOW}&transactionType[in]=${TransactionTypeEnum.BANK_TRANSACTION},${TransactionTypeEnum.CSV_TRANSACTION},${TransactionTypeEnum.CARD_TRANSACTION}`
        case TablesEnum.TRANSACTIONS_ALL:
            return `${notArchivedQueryString}&transactionType[in]=${TransactionTypeEnum.BANK_TRANSACTION},${TransactionTypeEnum.CSV_TRANSACTION},${TransactionTypeEnum.CARD_TRANSACTION}`
        case TablesEnum.EXPENSE_TRANSACTIONS:
            return `${notArchivedQueryString}&status[ne]=${TransactionStatusEnum.FAILED}&transactionType[in]=${TransactionTypeEnum.BANK_TRANSACTION},${TransactionTypeEnum.CSV_TRANSACTION},${TransactionTypeEnum.CARD_TRANSACTION}`
        case TablesEnum.TRANSACTIONS_ARCHIVE:
            return `${archivedQueryString}&transactionType[in]=${TransactionTypeEnum.BANK_TRANSACTION},${TransactionTypeEnum.CSV_TRANSACTION},${TransactionTypeEnum.CARD_TRANSACTION}`
        case TablesEnum.ATTACH_EXPENSE:
            const unattachableKinds = [ExpenseKindEnum.TRIP_FOLDER, ExpenseKindEnum.RECURRING_SUBSCRIPTION, ExpenseKindEnum.SUBSCRIPTION]
            return `${notArchivedQueryString}&kind[nin]=${unattachableKinds.join(",")}${notDeletedQueryString}&folderId[set]=false&status[in]=${ATTACHABLE_EXPENSE_STATUSES.join(",")}`
        case TablesEnum.TRIP_FOLDER_DRAFT:
            //* Draft folder table should only be visible to each user with their own requests
            return `${notArchivedQueryString}&kind[eq]=${ExpenseKindEnum.TRIP_FOLDER}${notDeletedQueryString}&isDraft[is]=true&requestedBy._id[eq]=${loggedInUserId}`
        default:
            return ""
    }
}

export const getOverlayTableDataSource = <T>(activeId: string | null, dataSource: Array<DraggableTableDataSourceType<T>>) => {
    const activeElement = dataSource.find((item) => (item._id ? item._id === activeId : item.key === activeId))

    if (!activeId || !activeElement) {
        return []
    }

    return [activeElement]
}
